/**
 * @file 本だなの資料への学習の記録シート (リーダー・本だなカードから開く共有部品)。
 *
 * @what: まなぶ人・日付・ページ範囲・時間 (分・任意)・ひとこと・写真を受けて
 *   StudyProgressLog を 1 件残す。始まりのページは同じ資料の前回の記録の続きを、
 *   終わりのページは開いていたページ (defaultToPage) を自動で入れる。資料への紐づけは
 *   detail (model/sourceStudyLog.ts) に持ち、保存後は取り消せる。
 * @why: 読む場所と記録する場所を一致させる (epic_23 §10)。呼び出し元は対象の資料と
 *   開いていたページを渡すだけで、学習者の選択・前回の続き・写真の送信はシートが
 *   引き受ける。まなぶ人の既定は最後に記録した人 (localStorage)。※なぜなら養育者の
 *   独学では毎回同じ人を選び直すことになるためである。
 * @failure: 学習者・前回の記録の読み込み失敗は 1 行で知らせ、手入力での記録はできる。
 *   写真が送れなければ記録も作らない (参照切れを作らない)。保存の失敗は入力を消さない。
 */

import { useEffect, useRef, useState } from "react";
import { formatLogDate, todayLocal } from "../api/dates";
import { memberLabel } from "../api/members";
import {
  deleteProgressLog,
  fetchProgressLogs,
  newProgressLogId,
  postProgressLog,
  type StudyProgressLogInput,
} from "../api/plans";
import { useLearners } from "../hooks/useLearners";
import { readLastLearner, writeLastLearner } from "../lib/lastLearner";
import { deleteStudyLogImage, uploadStudyLogImage } from "../lib/studyLogImage";
import { parseSourceStudyLog, serializeSourceStudyLog } from "../model/sourceStudyLog";
import { LearnerTabs } from "./LearnerTabs";
import { StudyDialog } from "./StudyDialog";
import "./study.css";

/** 前回の記録をさがす範囲 (直近の件数)。この中に無ければ手入力に任せる。 */
const LAST_LOG_SCAN_LIMIT = 30;

/** 時間 (分) の上限。サーバの検証 (StudyProgressLogMaxMinutes) と同じ値。 */
const MINUTES_MAX = 600;

export function StudyLogSheet({
  familyId,
  source,
  defaultToPage,
  onClose,
}: {
  familyId: string;
  /** 記録の対象 (本だなの資料)。title は detail に写す (資料が消えても記録が読める)。 */
  source: { id: string; title: string };
  /** 開いていたページ (リーダーから)。終わりのページの初期値になる。 */
  defaultToPage?: number;
  onClose: () => void;
}) {
  const learnersState = useLearners(familyId);
  const learners = learnersState.status === "ready" ? learnersState.learners : [];
  const [personId, setPersonId] = useState<string | null>(null);

  // 学習者が読めたら、最後に記録した人 (覚えが無ければ先頭) を選ぶ。
  useEffect(() => {
    if (learnersState.status !== "ready" || personId !== null) return;
    const remembered = readLastLearner(familyId);
    const found = learnersState.learners.find((l) => l.person_id === remembered);
    const next = found ?? learnersState.learners[0];
    if (next) setPersonId(next.person_id);
  }, [learnersState, personId, familyId]);

  const [date, setDate] = useState(todayLocal());
  const [fromPage, setFromPage] = useState("");
  const [toPage, setToPage] = useState(defaultToPage ? String(defaultToPage) : "");
  const [minutes, setMinutes] = useState("");
  const [memo, setMemo] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [lastLog, setLastLog] = useState<{ date: string; toPage: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ logId: string; personId: string } | null>(null);

  // この資料の前回の記録 (選んだ学習者の直近から探す)。見つかったら始まりのページに
  // 続きを入れる。学習者を替えたら入れ直す (別の人の続きに変わるため)。
  useEffect(() => {
    if (!personId) return;
    const controller = new AbortController();
    setLastLog(null);
    setFromPage("");
    fetchProgressLogs(familyId, personId, undefined, controller.signal, LAST_LOG_SCAN_LIMIT)
      .then((page) => {
        if (controller.signal.aborted) return;
        for (const log of page.items) {
          const d = parseSourceStudyLog(log);
          if (d?.source_id === source.id && log.to_page) {
            setLastLog({ date: log.date, toPage: log.to_page });
            setFromPage(String(log.to_page + 1));
            return;
          }
        }
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        // 前回が読めなくても手入力で記録できるので、止めない (詳細は console のみ)。
        console.error("[study] failed to load the last source log", err);
      });
    return () => controller.abort();
  }, [familyId, personId, source.id]);

  const clearPhoto = () => {
    setPhoto(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const minutesNumber = Number(minutes);
  const canSubmit =
    /^\d{4}-\d{2}-\d{2}$/.test(date) &&
    personId !== null &&
    !saving &&
    // from を入れたら to も必要 (サーバの検証と同じ)
    !(fromPage.trim() !== "" && toPage.trim() === "") &&
    (minutes.trim() === "" ||
      (Number.isInteger(minutesNumber) && minutesNumber >= 1 && minutesNumber <= MINUTES_MAX));

  const submit = async () => {
    if (!personId || !canSubmit) return;
    setSaving(true);
    setError(null);
    const input: StudyProgressLogInput = {
      id: newProgressLogId(),
      date,
      detail: serializeSourceStudyLog(source),
    };
    const from = Number(fromPage);
    const to = Number(toPage);
    if (fromPage.trim() !== "" && Number.isFinite(from) && from > 0) input.from_page = from;
    if (toPage.trim() !== "" && Number.isFinite(to) && to > 0) input.to_page = to;
    if (minutes.trim() !== "") input.minutes = minutesNumber;
    if (memo.trim()) input.memo = memo.trim();
    try {
      if (photo) {
        // 記録より先に画像を上げる (失敗したら記録も作らない = 参照切れを作らない)
        try {
          input.image_path = await uploadStudyLogImage(photo, familyId, input.id);
        } catch (err) {
          console.error("[study] failed to upload log image", err);
          setError("写真を送れませんでした。写真なしで記録するか、時間を置いてお試しください。");
          return;
        }
      }
      await postProgressLog(familyId, personId, input);
      writeLastLearner(familyId, personId);
      setSaved({ logId: input.id, personId });
    } catch (err) {
      // 逆向きの参照切れ (画像だけ上がって記録が無い) も残さない。
      if (input.image_path) deleteStudyLogImage(input.image_path);
      console.error("[study] failed to save a source log", err);
      setError("記録できませんでした。時間を置いてお試しください。");
    } finally {
      setSaving(false);
    }
  };

  const undo = async () => {
    if (!saved) return;
    setSaving(true);
    setError(null);
    try {
      await deleteProgressLog(familyId, saved.personId, saved.logId);
      setSaved(null);
    } catch (err) {
      console.error("[study] failed to undo a source log", err);
      setError("取り消せませんでした。時間を置いてお試しください。");
    } finally {
      setSaving(false);
    }
  };

  return (
    <StudyDialog title="学習を記録する" labelledBy="study-log-sheet-title" onClose={onClose}>
      <p className="study-dialog-sub">{source.title}</p>
      {saved ? (
        <div className="study-reading-just">
          <p>
            記録しました。
            <button
              type="button"
              className="study-link-btn"
              disabled={saving}
              onClick={() => void undo()}
            >
              取り消す
            </button>
          </p>
          <button type="button" className="study-primary-btn" onClick={onClose}>
            閉じる
          </button>
        </div>
      ) : (
        <>
          {learnersState.status === "error" ? (
            <p role="alert" className="study-error">
              まなぶ人の情報を読み込めませんでした。時間を置いて開き直してください。
            </p>
          ) : (
            <div className="study-learner-row">
              <span className="study-learner-row-label">まなぶ人</span>
              <LearnerTabs learners={learners} selectedId={personId} onSelect={setPersonId} />
            </div>
          )}
          {lastLog ? (
            <p className="study-muted">
              前回は {formatLogDate(lastLog.date, false)} に p.{lastLog.toPage}{" "}
              まで。つづきを入れました。
            </p>
          ) : null}
          <div className="study-log-form">
            <div className="study-log-form-row">
              <label className="study-log-field">
                <span>日付</span>
                <input
                  type="date"
                  value={date}
                  max={todayLocal()}
                  onChange={(e) => setDate(e.target.value)}
                />
              </label>
              <label className="study-log-field study-log-field--page">
                <span>ページ</span>
                <span className="study-log-pages">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={9999}
                    placeholder="24"
                    aria-label="始めたページ"
                    value={fromPage}
                    onChange={(e) => setFromPage(e.target.value)}
                  />
                  <span className="study-muted">〜</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={9999}
                    placeholder="41"
                    aria-label="進んだページ"
                    value={toPage}
                    onChange={(e) => setToPage(e.target.value)}
                  />
                </span>
              </label>
              <label className="study-log-field">
                <span>時間（分・任意）</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={MINUTES_MAX}
                  placeholder="30"
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                />
              </label>
            </div>
            <label className="study-log-field">
              <span>ひとこと</span>
              <input
                type="text"
                placeholder="例: 比の合成の別解がおもしろかった"
                maxLength={500}
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
              />
            </label>
            <div className="study-log-form-actions">
              <label className="study-log-photo-field">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
                />
                <span className="study-ghost-btn" role="button">
                  {photo ? "写真を変える" : "ノートの写真を足す"}
                </span>
              </label>
              {photo ? (
                <span className="study-log-photo-picked">
                  {photo.name}
                  <button type="button" className="study-link-btn" onClick={clearPhoto}>
                    外す
                  </button>
                </span>
              ) : null}
              <button
                type="button"
                className="study-save-btn"
                disabled={!canSubmit}
                onClick={() => void submit()}
              >
                記録する
              </button>
            </div>
          </div>
          {error ? (
            <p role="alert" className="study-error">
              {error}
            </p>
          ) : null}
          {personId !== null && learners.length > 0 ? (
            <p className="study-dialog-sub">
              {memberLabel(learners.find((l) => l.person_id === personId) ?? learners[0]!)}{" "}
              の記録として残します。
            </p>
          ) : null}
        </>
      )}
    </StudyDialog>
  );
}
